<template>
  <SfDropdown
    :dropdown-id="dropdownId"
    :is-open="isOpen"
    drawer-direction="left"
    variant="seamless"
    unstyled-trigger
    @dropdown-clicked="handleToggle"
    @dropdown-closed="handleClose"
  >
    <template #trigger>
      <SfButtonDs
        v-tooltip="I18n.t('communication.inbox.sort_tooltip')"
        size="sm"
        icon-after="caret-down"
        class="tw-normal-case tw-tracking-normal"
        :data-testid="`firm-inbox-sort-trigger-${section}`"
      >
        {{ currentTrigger.value }}
      </SfButtonDs>
    </template>
    <template #menu>
      <SfDropdownItem
        v-for="option in options"
        :key="option.id"
        :is-selected="option.id === modelValue"
        :has-checkmark="option.id === modelValue"
        :is-wrapped="false"
        :data-testid="`firm-inbox-sort-option-${section}-${option.id}`"
        @click="handleSelect(option.id)"
      >
        <span class="tw-normal-case tw-font-normal tw-tracking-normal">
          {{ option.prefix }}:
          <span class="tw-font-bold">{{ option.value }}</span>
        </span>
      </SfDropdownItem>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useDropdown } from '@app/shared/pinia/dropdown'
import {
  INBOX_SORT_OPTIONS,
  SORT_PREFIX_BY_OPTION,
  type InboxSortOption,
  type InboxSection,
} from '@app/application/communication/screens/inbox/constants/sort'

const I18n = useI18n()
const dropdownStore = useDropdown()

interface Props {
  section: InboxSection
  modelValue: InboxSortOption
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: InboxSortOption]
}>()

interface Option {
  id: InboxSortOption
  prefix: string
  value: string
}

const dropdownId = computed(() => `firm-inbox-sort-${props.section}`)
const isOpen = computed(
  () => dropdownStore.currentDropdownId === dropdownId.value
)

const options = computed<Option[]>(() =>
  INBOX_SORT_OPTIONS.map((id) => ({
    id,
    prefix: I18n.t(`communication.inbox.sort.prefix.${SORT_PREFIX_BY_OPTION[id]}`),
    value: I18n.t(`communication.inbox.sort.value.${id}`),
  }))
)

const currentTrigger = computed<Option>(
  () =>
    options.value.find((o) => o.id === props.modelValue) ?? {
      id: props.modelValue,
      prefix: '',
      value: '',
    }
)

function handleToggle() {
  dropdownStore.toggleDropdown(dropdownId.value)
}

function handleClose() {
  dropdownStore.closeDropdown()
}

function handleSelect(value: InboxSortOption) {
  if (value !== props.modelValue) {
    emit('update:modelValue', value)
  }
  dropdownStore.closeDropdown()
}
</script>
